import React, { useState } from 'react';
import { Button, Tooltip, Badge, Space, Popconfirm } from 'antd';
import { CloudSyncOutlined, CloudOutlined, CheckCircleOutlined, DisconnectOutlined, UploadOutlined } from '@ant-design/icons';
import { useHealthStore } from '@/stores/useHealthStore';
import { triggerManualSync, getSyncStats, addSyncStatusListener } from '@/services/sync';
import type { SyncStatus } from '@/types';

const SyncButton: React.FC = () => {
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('idle');
  const [syncDetails, setSyncDetails] = useState<string>('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [queueLength, setQueueLength] = useState(0);
  
  const { records, bpRecords, bsRecords } = useHealthStore();
  const localRecordCount = records.length + bpRecords.length + bsRecords.length;

  // 监听同步状态
  React.useEffect(() => {
    const unsubscribe = addSyncStatusListener((status, details) => {
      setSyncStatus(status);
      if (details) setSyncDetails(details);
      
      const stats = getSyncStats();
      setQueueLength(stats.queueLength);
      setIsSyncing(stats.isProcessing);
    });
    
    // 初始状态
    const stats = getSyncStats();
    setQueueLength(stats.queueLength);
    setIsSyncing(stats.isProcessing);
    
    return () => { unsubscribe(); };
  }, []);

  const handleSync = async () => {
    setIsSyncing(true);
    await triggerManualSync();
  };

  const handleForcePush = async () => {
    // 强制将所有本地数据推送到云端
    const event = new CustomEvent('forcePushAllData');
    window.dispatchEvent(event);
  };

  const getStatusConfig = () => {
    switch (syncStatus) {
      case 'syncing':
        return { 
          icon: <CloudSyncOutlined spin />, 
          color: 'processing', 
          text: '同步中',
          badgeColor: 'processing'
        };
      case 'synced':
        return { 
          icon: <CheckCircleOutlined />, 
          color: 'success', 
          text: '已同步',
          badgeColor: 'success'
        };
      case 'offline':
        return { 
          icon: <DisconnectOutlined />, 
          color: 'default', 
          text: '离线模式',
          badgeColor: 'default'
        };
      case 'pending':
        return { 
          icon: <CloudOutlined />, 
          color: 'warning', 
          text: '待同步',
          badgeColor: 'warning'
        };
      case 'error':
        return { 
          icon: <DisconnectOutlined />, 
          color: 'error', 
          text: '同步失败',
          badgeColor: 'error'
        };
      default:
        return { 
          icon: <CloudOutlined />, 
          color: 'default', 
          text: '未配置',
          badgeColor: 'default'
        };
    }
  };

  const config = getStatusConfig();

  return (
    <Space>
      {/* 显示本地数据数量 */}
      {localRecordCount > 0 && syncStatus === 'offline' && (
        <Badge count={localRecordCount} style={{ backgroundColor: '#faad14' }}>
          <span style={{ color: '#faad14' }}>本地数据</span>
        </Badge>
      )}
      
      {/* 一键推送按钮（仅在离线模式或有本地数据时显示） */}
      {(syncStatus === 'offline' || localRecordCount > 0) && (
        <Popconfirm
          title="强制推送数据"
          description={`将 ${localRecordCount} 条本地记录推送到云端。确定吗？`}
          onConfirm={handleForcePush}
          okText="确定"
          cancelText="取消"
        >
          <Button 
            type="primary" 
            icon={<UploadOutlined />}
            size="small"
            danger
          >
            推送本地数据
          </Button>
        </Popconfirm>
      )}
      
      {/* 同步按钮 */}
      <Tooltip title={syncDetails || config.text}>
        <Badge dot={queueLength > 0} color={config.badgeColor}>
          <Button
            type="text"
            icon={config.icon}
            onClick={handleSync}
            loading={isSyncing}
            disabled={syncStatus === 'offline' || syncStatus === 'idle'}
            size="small"
          >
            {config.text}
            {queueLength > 0 && ` (${queueLength})`}
          </Button>
        </Badge>
      </Tooltip>
    </Space>
  );
};

export default SyncButton;
